<template>
  <div class="u-time-picker w-full">
    <Popover v-slot="{ close }" class="relative block">
      <PopoverButton
        :disabled="disabled"
        :class="[
          'u-time-picker-trigger flex w-full rin items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
          'bg-[var(--theme-surface)] border-[var(--theme-border)] text-[var(--theme-text)]',
          'placeholder-[var(--theme-text-tertiary)]',
          'hover:border-[var(--theme-text-tertiary)]/40 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary-alpha)] focus:border-[var(--theme-primary)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          inputClass,
          error ? 'u-time-picker-trigger--error border-[var(--theme-error)] ring-2 ring-[var(--theme-error)]/20' : ''
        ]"
      >
        <span class="truncate">{{ displayText }}</span>
        <span class="shrink-0 text-[var(--theme-text-tertiary)]" aria-hidden="true">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </span>
      </PopoverButton>
      <PopoverPanel
        class="u-time-picker-panel absolute left-0 z-50 mt-1 min-w-[200px] rounded-xl border border-[var(--theme-border)] bg-[var(--theme-card)] p-4 shadow-xl"
      >
        <div class="space-y-3">
          <p class="text-xs font-medium uppercase tracking-wider text-[var(--theme-text-tertiary)]">Select time</p>
          <input
            ref="timeInputRef"
            type="time"
            :value="internalTime"
            step="60"
            class="u-time-picker-input w-full rounded-lg border border-[var(--theme-border)] bg-[var(--theme-surface)] px-3 py-2.5 text-sm text-[var(--theme-text)] focus:border-[var(--theme-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary-alpha)]"
            @input="onTimeInput"
          />
          <div class="flex justify-end gap-2 pt-1">
            <button
              type="button"
              class="rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--theme-text-tertiary)] hover:bg-[var(--theme-surface)] hover:text-[var(--theme-text)] transition-colors"
              @click="close"
            >
              Done
            </button>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';

function parseTime(str) {
  if (!str || typeof str !== 'string') return '';
  const trimmed = str.trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return '';
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (h < 0 || h > 23 || m < 0 || m > 59) return '';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function toHHMM(str) {
  const t = parseTime(str);
  return t || '';
}

export default {
  name: 'UTimePicker',
  components: { Popover, PopoverButton, PopoverPanel },
  props: {
    modelValue: { type: [String, null], default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    inputClass: { type: [String, Object, Array], default: '' },
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const timeInputRef = ref(null);
    const internalTime = computed(() => toHHMM(props.modelValue));

    watch(() => props.modelValue, (val) => {
      const t = toHHMM(val);
      if (t && t !== internalTime.value) {
        // internalTime is computed from props so it updates automatically
      }
    }, { immediate: true });

    const displayText = computed(() => {
      const t = toHHMM(props.modelValue);
      if (t) {
        const [h, m] = t.split(':').map(Number);
        const h12 = h % 12 || 12;
        const ampm = h < 12 ? 'AM' : 'PM';
        return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
      }
      return props.placeholder || 'Select time';
    });

    function onTimeInput(e) {
      const v = e.target?.value;
      const out = toHHMM(v);
      emit('update:modelValue', out || null);
      emit('change', out || null);
    }

    return { timeInputRef, internalTime, displayText, onTimeInput };
  },
};
</script>

<style scoped>
.u-time-picker-panel {
  color: var(--theme-text);
}
</style>

<!-- Admin panel: match admin form styling -->
<style>
.admin-panel .u-time-picker .u-time-picker-trigger {
  background-color: #ffffff;
  border-color: #e2e8f0;
  color: #1e293b;
}
.admin-panel .u-time-picker .u-time-picker-trigger:hover {
  border-color: #cbd5e1;
}
.admin-panel .u-time-picker .u-time-picker-trigger:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.admin-panel .u-time-picker .u-time-picker-trigger .shrink-0 {
  color: #94a3b8;
}
.admin-panel .u-time-picker .u-time-picker-trigger.u-time-picker-trigger--error {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}
.admin-panel .u-time-picker .u-time-picker-panel {
  background-color: #ffffff;
  border-color: #e2e8f0;
  color: #334155;
}
.admin-panel .u-time-picker .u-time-picker-input {
  background-color: #f8fafc;
  border-color: #e2e8f0;
  color: #1e293b;
}
.admin-panel .u-time-picker .u-time-picker-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
</style>
